import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Search } from "lucide-react";

export const WEEK = [[0, "Lun"], [1, "Mar"], [2, "Mer"], [3, "Gio"], [4, "Ven"], [5, "Sab"], [6, "Dom"]];
export const ROTATIONS = { daily: "Cambia ogni giorno", weekly: "Stessa persona per la settimana", period: "Stessa persona per tutto il calendario" };
const sname = (s) => `${s.last_name || ""} ${s.first_name || ""}`.trim();

export function MansioneSchedulingFields({ form, setForm, students }) {
  const toggleDay = (d) => {
    const set = new Set(form.days);
    set.has(d) ? set.delete(d) : set.add(d);
    setForm({ ...form, days: [...set].sort() });
  };
  return (
    <div className="space-y-4 border-t border-stone-100 pt-4">
      <div className="text-[11px] uppercase tracking-widest text-stone-500">Generazione automatica</div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Risorse (persone per turno)</Label>
          <Input type="number" min={1} max={50} value={form.resources} onChange={(e) => setForm({ ...form, resources: Math.max(1, Number(e.target.value) || 1) })} className="mt-1.5" data-testid="mansione-resources"/>
        </div>
        <div>
          <Label>Rotazione</Label>
          <select value={form.rotation} onChange={(e) => setForm({ ...form, rotation: e.target.value })} data-testid="mansione-rotation"
            className="mt-1.5 h-9 w-full rounded-md border border-stone-200 bg-white px-2 text-sm">
            {Object.entries(ROTATIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>
      <div>
        <Label>Giorni in cui si svolge</Label>
        <div className="flex gap-1.5 mt-1.5 flex-wrap">
          {WEEK.map(([d, l]) => (
            <button key={d} type="button" onClick={() => toggleDay(d)} data-testid={`mansione-day-${d}`}
              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${form.days.includes(d) ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200 text-stone-600"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <Label>Chi può svolgerla</Label>
        <div className="flex gap-1.5 mt-1.5">
          {[["", "Tutti"], ["M", "Solo uomini"], ["F", "Solo donne"]].map(([g, l]) => (
            <button key={g || "all"} type="button" onClick={() => setForm({ ...form, gender: g })} data-testid={`mansione-gender-${g || "all"}`}
              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${(form.gender || "") === g ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200 text-stone-600"}`}>{l}</button>
          ))}
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={!!form.all_students} onChange={(e) => setForm({ ...form, all_students: e.target.checked })} data-testid="mansione-all-students"/>
        <span>Coinvolge tutti gli studenti (es. Pulizie speciali)</span>
      </label>
      <TimeSlotsEditor slots={form.time_slots} onChange={(time_slots) => setForm({ ...form, time_slots })}/>
      <ExcludedPicker students={students} value={form.excluded_student_ids} onChange={(ids) => setForm({ ...form, excluded_student_ids: ids })}/>
    </div>
  );
}

function TimeSlotsEditor({ slots, onChange }) {
  const set = (i, k, v) => onChange(slots.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  return (
    <div>
      <Label>Fasce orarie (facoltative)</Label>
      <div className="space-y-2 mt-1.5">
        {slots.map((s, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`mansione-slot-${i}`}>
            <Input type="time" value={s.start_time || ""} onChange={(e) => set(i, "start_time", e.target.value)} data-testid={`mansione-start-${i}`}/>
            <span className="text-stone-400">→</span>
            <Input type="time" value={s.end_time || ""} onChange={(e) => set(i, "end_time", e.target.value)} data-testid={`mansione-end-${i}`}/>
            {slots.length > 1 && <button type="button" onClick={() => onChange(slots.filter((_, j) => j !== i))} data-testid={`mansione-slot-del-${i}`}><X size={14} className="text-stone-500"/></button>}
          </div>
        ))}
        <button type="button" onClick={() => onChange([...slots, { start_time: "", end_time: "" }])} className="text-xs text-[#1C352D] underline" data-testid="mansione-slot-add">+ Aggiungi fascia (es. 14:30–15:00)</button>
      </div>
    </div>
  );
}

function ExcludedPicker({ students, value, onChange }) {
  const [q, setQ] = useState("");
  const byId = useMemo(() => Object.fromEntries(students.map((s) => [s.id, s])), [students]);
  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return students.filter((s) => !value.includes(s.id) && `${s.first_name} ${s.last_name} ${s.matricola || ""}`.toLowerCase().includes(t)).slice(0, 8);
  }, [q, students, value]);
  return (
    <div>
      <Label>Studenti esclusi da questa mansione</Label>
      <p className="text-[11px] text-stone-500 mt-0.5">Es. chi non vive in residenza non pulisce le camerate.</p>
      <div className="relative mt-1.5">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400"/>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca studente da escludere…" className="pl-8" data-testid="mansione-exclude-search"/>
        {matches.length > 0 && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-stone-200 rounded-md shadow-md max-h-56 overflow-y-auto">
            {matches.map((s) => (
              <button key={s.id} type="button" onClick={() => { onChange([...value, s.id]); setQ(""); }} data-testid={`mansione-exclude-opt-${s.id}`}
                className="block w-full text-left px-3 py-1.5 text-sm hover:bg-stone-50">{sname(s)} <span className="text-xs text-stone-400">{s.matricola}</span></button>
            ))}
          </div>
        )}
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="mansione-excluded-list">
          {value.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-800 border border-red-200">
              {byId[id] ? sname(byId[id]) : "Studente rimosso"}
              <button type="button" onClick={() => onChange(value.filter((x) => x !== id))} data-testid={`mansione-exclude-remove-${id}`}><X size={11}/></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
